import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { User, Lock, Clock, ArrowLeft, Save, Eye, EyeOff, CheckCircle, AlertCircle, Shield, LogOut } from 'lucide-react';
import PublicNavigation from '../../components/ui/PublicNavigation';
import { clientAuthService } from '../../services/clientAuthService';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';


export default function ClientProfile() {
  const navigate = useNavigate();

  // Profile state
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');

  // Profile edit state
  const [profileForm, setProfileForm] = useState({ name: '', email: '', phone: '', address: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  // Password state
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Login history
  const [loginHistory, setLoginHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (!clientAuthService?.isAuthenticated()) {
      navigate('/client-login');
      return;
    }
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const clientData = JSON.parse(localStorage.getItem('client_data') || '{}');
      if (!clientData?.id) { navigate('/client-login'); return; }

      const { data, error } = await supabase
        ?.from('clients')
        ?.select('id, name, email, phone, address, loyalty_points_total, created_at')
        ?.eq('id', clientData?.id)
        ?.single();

      if (error || !data) { navigate('/client-login'); return; }

      setClient(data);
      setProfileForm({
        name: data?.name || '',
        email: data?.email || '',
        phone: data?.phone || '',
        address: data?.address || ''
      });

      loadLoginHistory(clientData?.id);
    } catch (err) {
      console.error('loadProfile error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadLoginHistory = async (clientId) => {
    try {
      setHistoryLoading(true);
      const { data } = await supabase?.rpc('get_client_login_history', {
        p_client_id: clientId,
        p_limit: 5
      });
      setLoginHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('loadLoginHistory error:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleProfileSave = async (e) => {
    e?.preventDefault();
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      const clientData = JSON.parse(localStorage.getItem('client_data') || '{}');
      const { data, error } = await supabase?.rpc('update_client_profile', {
        p_client_id: clientData?.id,
        p_name: profileForm?.name?.trim(),
        p_email: profileForm?.email?.trim(),
        p_phone: profileForm?.phone?.trim(),
        p_address: profileForm?.address?.trim()
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Erreur lors de la mise à jour');

      // Update localStorage
      const updated = { ...clientData, name: profileForm?.name, email: profileForm?.email, phone: profileForm?.phone };
      localStorage.setItem('client_data', JSON.stringify(updated));
      setClient(prev => ({ ...prev, ...profileForm }));
      setProfileMsg({ type: 'success', text: 'Profil mis à jour avec succès !' });
    } catch (err) {
      setProfileMsg({ type: 'error', text: err?.message || 'Erreur lors de la mise à jour' });
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e?.preventDefault();
    setPwMsg(null);

    if (pwForm?.newPw?.length < 8) {
      setPwMsg({ type: 'error', text: 'Le nouveau mot de passe doit contenir au moins 8 caractères.' });
      return;
    }
    if (pwForm?.newPw !== pwForm?.confirm) {
      setPwMsg({ type: 'error', text: 'Les mots de passe ne correspondent pas.' });
      return;
    }

    setPwSaving(true);
    try {
      const clientData = JSON.parse(localStorage.getItem('client_data') || '{}');
      const { data, error } = await supabase?.rpc('change_client_password', {
        p_client_id: clientData?.id,
        p_current_password: pwForm?.current,
        p_new_password: pwForm?.newPw
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Erreur lors du changement de mot de passe');

      setPwMsg({ type: 'success', text: 'Mot de passe modifié avec succès !' });
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (err) {
      setPwMsg({ type: 'error', text: err?.message || 'Erreur lors du changement de mot de passe' });
    } finally {
      setPwSaving(false);
    }
  };

  const handleLogout = async () => {
    await clientAuthService?.signOut();
    navigate('/client-login');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr)?.toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return '—'; }
  };

  const tabs = [
    { id: 'profile', label: 'Mon profil', icon: User },
    { id: 'security', label: 'Sécurité', icon: Shield },
    { id: 'history', label: 'Connexions', icon: Clock }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-4 border-green-600 border-t-transparent rounded-full mx-auto mb-3"></div>
          <p className="text-gray-600 text-sm">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Mon Compte - ADT Express</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50 pt-16 md:pt-[136px]">
        <PublicNavigation />

        <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Link
                to="/client-dashboard"
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-green-600 transition-colors"
              >
                <ArrowLeft size={16} />
                <span>Tableau de bord</span>
              </Link>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 transition-colors"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>

          {/* Profile Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 sm:p-6 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <User size={26} className="text-green-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{client?.name || 'Client'}</h1>
                <p className="text-sm text-gray-500">{client?.email || client?.phone}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium">
                    {client?.loyalty_points_total || 0} points fidélité
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6">
            {tabs?.map(tab => {
              const Icon = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === tab?.id
                      ? 'bg-white text-green-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon size={15} />
                  <span className="hidden sm:inline">{tab?.label}</span>
                </button>
              );
            })}
          </div>

          {/* Tab: Profile */}
          {activeTab === 'profile' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 sm:p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-5">Mes informations</h2>

              {profileMsg && (
                <div className={`flex items-center gap-2 p-3 rounded-lg mb-5 text-sm ${
                  profileMsg?.type === 'success' ?'bg-green-50 text-green-800 border border-green-200' :'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {profileMsg?.type === 'success'
                    ? <CheckCircle size={16} />
                    : <AlertCircle size={16} />}
                  {profileMsg?.text}
                </div>
              )}

              <form onSubmit={handleProfileSave} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Nom complet</label>
                    <input
                      type="text"
                      value={profileForm?.name}
                      onChange={e => setProfileForm(p => ({ ...p, name: e?.target?.value }))}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Votre nom"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Téléphone</label>
                    <input
                      type="tel"
                      value={profileForm?.phone}
                      onChange={e => setProfileForm(p => ({ ...p, phone: e?.target?.value }))}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="0612345678"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={profileForm?.email}
                    onChange={e => setProfileForm(p => ({ ...p, email: e?.target?.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="email@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Adresse</label>
                  <input
                    type="text"
                    value={profileForm?.address}
                    onChange={e => setProfileForm(p => ({ ...p, address: e?.target?.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Votre adresse complète"
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={profileSaving}
                    className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {profileSaving ? (
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    ) : (
                      <Save size={16} />
                    )}
                    {profileSaving ? 'Enregistrement...' : 'Modifier mes informations'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Tab: Security */}
          {activeTab === 'security' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-5">
                <Lock size={20} className="text-green-600" />
                <h2 className="text-lg font-bold text-gray-900">Sécurité</h2>
              </div>

              {pwMsg && (
                <div className={`flex items-center gap-2 p-3 rounded-lg mb-5 text-sm ${
                  pwMsg?.type === 'success' ?'bg-green-50 text-green-800 border border-green-200' :'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {pwMsg?.type === 'success'
                    ? <CheckCircle size={16} />
                    : <AlertCircle size={16} />}
                  {pwMsg?.text}
                </div>
              )}

              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Mot de passe actuel</label>
                  <div className="relative">
                    <input
                      type={showCurrent ? 'text' : 'password'}
                      value={pwForm?.current}
                      onChange={e => setPwForm(p => ({ ...p, current: e?.target?.value }))}
                      required
                      className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Votre mot de passe actuel"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrent(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Nouveau mot de passe</label>
                  <div className="relative">
                    <input
                      type={showNew ? 'text' : 'password'}
                      value={pwForm?.newPw}
                      onChange={e => setPwForm(p => ({ ...p, newPw: e?.target?.value }))}
                      required
                      minLength={8}
                      className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Minimum 8 caractères"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {pwForm?.newPw?.length > 0 && pwForm?.newPw?.length < 8 && (
                    <p className="text-xs text-red-500 mt-1">Minimum 8 caractères requis</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirmer le nouveau mot de passe</label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={pwForm?.confirm}
                      onChange={e => setPwForm(p => ({ ...p, confirm: e?.target?.value }))}
                      required
                      className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Répétez le nouveau mot de passe"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {pwForm?.confirm?.length > 0 && pwForm?.newPw !== pwForm?.confirm && (
                    <p className="text-xs text-red-500 mt-1">Les mots de passe ne correspondent pas</p>
                  )}
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={pwSaving}
                    className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {pwSaving ? (
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    ) : (
                      <Lock size={16} />
                    )}
                    {pwSaving ? 'Modification...' : 'Modifier mon mot de passe'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Tab: Login History */}
          {activeTab === 'history' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-5">
                <Clock size={20} className="text-green-600" />
                <h2 className="text-lg font-bold text-gray-900">Historique des connexions</h2>
              </div>

              {historyLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-green-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-sm text-gray-500">Chargement...</p>
                </div>
              ) : loginHistory?.length === 0 ? (
                <div className="text-center py-10">
                  <Clock size={40} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">Aucun historique de connexion disponible.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {loginHistory?.map((entry, idx) => (
                    <div
                      key={entry?.id || idx}
                      className={`flex items-start gap-3 p-3 rounded-lg border ${
                        idx === 0 ? 'border-green-200 bg-green-50' : 'border-gray-100 bg-gray-50'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        idx === 0 ? 'bg-green-100' : 'bg-gray-200'
                      }`}>
                        <Clock size={14} className={idx === 0 ? 'text-green-600' : 'text-gray-500'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-900">
                            {formatDate(entry?.login_date)}
                          </span>
                          {idx === 0 && (
                            <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">
                              Dernière connexion
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                          {entry?.ip_address && (
                            <span className="text-xs text-gray-500">IP: {entry?.ip_address}</span>
                          )}
                          {entry?.browser && (
                            <span className="text-xs text-gray-500 truncate max-w-[200px]">{entry?.browser}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
